"use client";

import { Microscope, Flame, Cog, Activity, Crosshair, Wind, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Fgasregulations() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const certifications = ["REFCOM CERTIFIED", "F-GAS COMPLIANT", "ISO 9001 QUALITY", "SAFETY ASSURED"];
  const features = [{
    id: "R01",
    icon: Microscope,
    title: "LEAK DETECTION",
    description: "Advanced electronic sensing to identify micro-leaks in refrigerant lines before pressure loss occurs."
  }, {
    id: "R02",
    icon: Cog,
    title: "SYSTEM AUDITS",
    description: "Comprehensive performance evaluations to ensure your climate control meets all legal efficiency standards."
  }, {
    id: "R03",
    icon: Flame,
    title: "GAS RECOVERY",
    description: "Environmentally responsible refrigerant recovery and disposal following strict industry safety protocols."
  }, {
    id: "R04",
    icon: Activity,
    title: "PRESSURE TESTING",
    description: "Rigorous nitrogen pressure testing to verify system integrity and prevent future environmental hazards."
  }, {
    id: "R05",
    icon: Crosshair,
    title: "COMPLIANCE LOGS",
    description: "Detailed digital record-keeping for all maintenance and gas handling to satisfy regulatory requirements."
  }, {
    id: "R06",
    icon: Wind,
    title: "EFFICIENCY TUNING",
    description: "Optimizing airflow and thermal output to ensure maximum comfort while minimizing energy consumption."
  }];
  return <section data-section-id="3990" id="fgasregulations" ref={sectionRef} className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 px-4 md:px-8">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "40px 40px"
    }} aria-hidden="true" />

      {}
      <div className="absolute top-8 left-4 right-4 md:left-8 md:right-8 flex items-center pointer-events-none" aria-hidden="true">
        <span className="text-primary font-mono text-lg leading-none">+</span>
        <div className="flex-1 border-t border-dashed border-border mx-2" />
        <span className="text-primary font-mono text-lg leading-none">+</span>
      </div>

      <div className="relative max-w-7xl mx-auto pt-8">
        {}
        <div className={`mb-12 md:mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs md:text-sm text-primary tracking-widest uppercase">
            // 02 — REGULATORY COMPLIANCE
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className={`transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <h2 className="font-default font-bold text-5xl sm:text-6xl md:text-7xl lg:text-6xl xl:text-7xl uppercase leading-[0.95] tracking-tight text-foreground">
                  Strict safety.
                  <br />
                  <span className="text-primary">Total</span>
                  <br />
                  compliance.
                </h2>

                <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
                  At Coastline Air Conditioning Services, we prioritize safety and environmental responsibility. Our certified technicians 
                  adhere to the highest F-Gas regulations, ensuring your climate control systems are 
                  efficient, legal, and perfectly maintained.
                </p>

                {}
                <div className="mt-10 space-y-3">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px w-8 bg-primary" />
                    <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                      Standards & Certifications
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {certifications.map((cert, i) => <Badge key={cert} data-index={i} variant="outline" className="font-mono text-xs border-border text-foreground bg-card/40 px-3 py-1.5 w-fit tracking-wider">
                        {cert}
                      </Badge>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <div key={feature.id} data-index={i} className={`group relative bg-card border border-border hover:border-primary transition-all duration-500 p-6 md:p-7 overflow-hidden ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
                transitionDelay: isVisible ? `${200 + i * 80}ms` : "0ms",
                backgroundImage: "linear-gradient(135deg, var(--card) 0%, var(--card) 60%, color-mix(in oklab, var(--card) 92%, var(--primary)) 100%)"
              }}>
                    {}
                    <div className="absolute top-4 left-5 font-mono text-xs text-muted-foreground tracking-wider">
                      {feature.id}
                    </div>

                    {}
                    <div className="absolute top-3 right-3 w-3 h-3 border-t border-r border-border group-hover:border-primary transition-colors duration-500" aria-hidden="true" />

                    {}
                    <div className="mt-8 mb-5">
                      <Icon className="w-9 h-9 text-primary group-hover:scale-110 transition-transform duration-500 stroke-[1.25]" aria-hidden="true" />
                    </div>

                    {}
                    <h3 className="font-default font-semibold text-base md:text-lg uppercase tracking-wider text-foreground mb-2">
                      {feature.title}
                    </h3>

                    {}
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {feature.description}
                    </p>

                    {}
                    <div className="relative pt-4 mt-auto">
                      <div className="absolute top-0 left-0 right-0 h-px border-t border-dashed border-border group-hover:border-primary transition-colors duration-500" />
                      <Link to="/MaintenancePage" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-all duration-500 opacity-70 group-hover:opacity-100">
                        <span>Learn More</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
                      </Link>
                    </div>

                    {}
                    <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  boxShadow: "inset 0 0 0 1px var(--primary), 0 0 24px -8px var(--primary)"
                }} aria-hidden="true" />
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-8 left-4 right-4 md:left-8 md:right-8 flex items-center pointer-events-none" aria-hidden="true">
        <span className="text-primary font-mono text-lg leading-none">+</span>
        <div className="flex-1 border-t border-dashed border-border mx-2" />
        <span className="text-primary font-mono text-lg leading-none">+</span>
      </div>
    </section>;
}